<script setup lang="ts">
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  computed,
} from 'vue';
import { EditorState, Extension } from '@codemirror/state';
import {
  EditorView,
  lineNumbers,
  placeholder as cmPlaceholder,
  keymap,
} from '@codemirror/view';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from '@codemirror/commands';
import {
  syntaxHighlighting,
  defaultHighlightStyle,
} from '@codemirror/language';
import { MergeView } from '@codemirror/merge';
import { javascript } from '@codemirror/lang-javascript';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { json as jsonLang } from '@codemirror/lang-json';
import { yaml } from '@codemirror/lang-yaml';

type Dictionary<T> = Record<string, T>;

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    code?: string;
    value?: string;
    name?: string;
    mode?: string;
    placeholder?: string;
    merge?: boolean;
    options?: Dictionary<any>;
    globalOptions?: Dictionary<any>;
    events?: string[];
    globalEvents?: string[];
  }>(),
  {
    name: 'codemirror',
    mode: 'javascript',
    merge: false,
    placeholder: '',
    options: () => ({}),
    globalOptions: () => ({}),
    events: () => [],
    globalEvents: () => [],
  },
);

const emit = defineEmits<{
  (event: 'ready', view: any): void;
  (event: 'input', value: string): void;
  (event: 'change', value: string): void;
  (event: 'update:modelValue', value: string): void;
}>();

const wrapper = ref<HTMLElement | null>(null);
let view: EditorView | null = null;
let isInternalUpdate = false;

const content = ref('');

const wrapperClass = computed(() => ({ merge: props.merge }));

function langExt() {
  switch (props.mode) {
    case 'json':
      return jsonLang();
    case 'yaml':
      return yaml();
    case 'css':
      return css();
    case 'html':
      return html();
    default:
      return javascript();
  }
}

function buildExtensions(): Extension[] {
  const extensions: Extension[] = [
    lineNumbers(),
    history(),
    keymap.of([indentWithTab, ...defaultKeymap, ...historyKeymap]),
    syntaxHighlighting(defaultHighlightStyle),
    langExt(),
    EditorView.updateListener.of((value) => {
      if (value.docChanged) {
        const documentContent = value.state.doc.toString();
        isInternalUpdate = true;
        emit('update:modelValue', documentContent);
        emit('input', documentContent);
        emit('change', documentContent);
        queueMicrotask(() => (isInternalUpdate = false));
      }
    }),
  ];
  if (props.placeholder) {
    extensions.push(cmPlaceholder(props.placeholder));
  }
  if (props.options?.readOnly) {
    extensions.push(EditorView.editable.of(false));
  }
  return extensions;
}

function initialize() {
  if (!wrapper.value) {
    return;
  }

  const baseDoc = props.modelValue ?? props.code ?? props.value ?? '';
  if (props.merge) {
    const mergeView = new MergeView({
      parent: wrapper.value!,
      a: {
        doc: props.code ?? '',
        extensions: [EditorView.editable.of(false), ...buildExtensions()],
      },
      b: { doc: baseDoc, extensions: buildExtensions() },
    });
    view = mergeView.b;
  } else {
    const state = EditorState.create({
      doc: baseDoc,
      extensions: buildExtensions(),
    });
    view = new EditorView({ state, parent: wrapper.value! });
  }
  emit('ready', view);
}

function destroy() {
  view?.destroy();
  view = null;
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (!view || isInternalUpdate) {
      return;
    }
    const current = view.state.doc.toString();
    const val = newValue ?? '';
    if (val !== current) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: val },
      });
    }
  },
);

watch(
  () => props.code,
  (newValue) => {
    if (!view || isInternalUpdate) {
      return;
    }
    const current = view.state.doc.toString();
    const val = newValue ?? '';
    if (val !== current) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: val },
      });
    }
  },
);

onMounted(() => nextTick(initialize));
onBeforeUnmount(destroy);
</script>

<template>
  <div class="vue-codemirror" :class="wrapperClass">
    <div ref="wrapper" />
  </div>
</template>

<style scoped lang="scss">
.vue-codemirror {
  height: 100%;
  .cm-editor {
    height: 100%;
    border: 1px solid #ccc;
    border-radius: 0.5rem;
    font-family: Menlo, monospace;
    font-size: 14px;
  }
}
</style>
